import { ActionResultLong } from '@portal/models/actionResultLong';
import { BlockElementShared } from '@portal/models/blockElementShared';
import { CompanyCreateRequest } from '@portal/models/companyCreateRequest';
import { CompanyShared } from '@portal/models/companyShared';
import { LegalMemberShared } from '@portal/models/legalMemberShared';
import { PromocodePaginationRequest } from '@portal/models/promocodePaginationRequest';
import { UserFileResponse } from '@portal/models/userFileResponse';
import TaxEnum = CompanyShared.TaxEnum;
import TaxationSystemEnum = CompanyShared.TaxationSystemEnum;
import CompanyProductTypesEnum = CompanyCreateRequest.CompanyProductTypesEnum;
import LangEnum = PromocodePaginationRequest.LangEnum;
import TypeEnum = UserFileResponse.TypeEnum;
import ServiceEnum = ActionResultLong.ServiceEnum;

type Translation<T extends string> = { [P in T]: string };

export interface CoreInternalization {
  language: 'en' | 'ru' | 'fr' | 'ar' | 'hi' | 'khr' | 'cny';
  availableLanguages: Translation<LangEnum>;
  lang: string;
  filters: {
    title: string;
    search: string;
    reset: string;
  };
  popups: {
    confirmation: {
      title: string;
      body: string;
      buttons: {
        cancel: string;
        confirm: string;
      };
    };
  };
  services: Translation<ServiceEnum>;
  shared: {
    allFieldsRequired: string;
    errors: {
      maxLength: string;
      minLength: string;
      pattern: string;
      patternInfo: string;
    };
    url: {
      label: string;
      urlTitle: string;
      url: string;
    };
    actions: string;
    emptyOption: string;
    no: string;
    print: string;
    updateError: string;
    updateSuccess: string;
    yes: string;
    title: string;
    description: string;
    additionalDescription: string;
    createdAt: string;
    updatedAt: string;
    startedAt: string;
    buyerPrice: string;
    finishedAt: string;
    value: string;
    active: string;
    mainInfo: string;
    elements: string;
    id: string;
  };
  shell: {
    exit: string;
    menu: string;
    helpUrl: string;
  };
  companies: {
    buyerAddresses: string;
    addNew: string;
    filters: string;
    label: string;
    locked: string;
    tax: Translation<TaxEnum>;
    taxationSystem: Translation<TaxationSystemEnum>;
    companyProductTypes: Translation<CompanyProductTypesEnum>;
    unlockNotification: string;
    table: {
      email: string;
      phone: string;
      companyType: string;
      name: string;
    };
    edit: {
      buyer: {
        shortInfo: {
          averageCheck: string;
          creditRating: string;
          ordersAmount: string;
        };
      };
      agent: {
        shortInfo: {
          addedClients: string;
          agentRating: string;
          requestsProceeded: string;
        };
      };
      shortInfo: {
        totalProducts: string;
        productsAvailable: string;
        ordersPerMonth: string;
        monthRevenue: string;
      };
      allStatistics: string;
      tabs: {
        company: string;
        agent: string;
        buyer: string;
        seller: string;
      };
      seller: {
        logisticsSettings: {
          settings: string;
          deliveryCost: string;
          deliveryName: string;
          tariffName: string;
          note: string;
        };
        tabs: {
          general: string;
          warehouses: string;
          workingHours: string;
          employees: string;
          sla: string;
          additionalInfo: string;
          profile: string;
        };
      };
      amountEmployees: string;
      profileType: string;
      lockingProfile: string;
      lockProfile: string;
      unlockProfile: string;
      lockProfileNotification: string;
      hideProfileNotification: string;
      showProfile: string;
      hideProfile: string;
      members: string;
      memberAdd: string;
      password: string;
      passwordSendNew: string;
      companyInfo: string;
      moreInfo: string;
      shopName: string;
      shopIcon: string;
      shopImages: string;
      lockProfileSuccess: string;
      unlockProfileSuccess: string;
      limitation: string;
    };
    add: {
      mid: string;
      organizationTypeId: string;
      vatid: string;
      termsAndConditions: string;
      maroofLink: string;
      addressLine: string;
      district: string;
      city: string;
      country: string;
      countryCode: string;
      description: string;
      email: string;
      firstName: string;
      fullCompanyName: string;
      label: string;
      lang: string;
      lastName: string;
      locationInfo: string;
      managerCountryCode: string;
      managerEmail: string;
      managerNationalNumber: string;
      managerInfo: string;
      middleName: string;
      nationalNumber: string;
      organizationType: string;
      postalCode: string;
      region: string;
      regionId: string;
      shortName: string;
      siteUrl: string;
      submit: string;
      tax: string;
      taxationSystem: string;
      tin: string;
      disputeProcess: string;
      merchantCategory: string;
      commercialRegistrationDate: string;
      commercialRegistrationExpiryDate: string;
      companyProductType: string;
      subscriptionType: string;
      companyType: string;
      socialLinks: string;
      twitter: string;
      instagram: string;
      whatsApp: string;
      facebook: string;
      externalMerchantId: string;
    };
    members: {
      noRoles: string;
      adding: string;
      addAsEmployee: string;
      agentStatus: Translation<LegalMemberShared.ProfileStatusEnum>;
      table: {
        email: string;
        fullName: string;
        noRecords: string;
        phone: string;
        status: string;
      };
      edit: {
        accessSettings: string;
        changedPassword: string;
        email: string; // TODO: move to shared
        firstName: string;
        lastName: string;
        lockMemberNotification: string;
        unlockMemberNotification: string;
        memberInfo: string;
        middleName: string;
        passwordReset: string;
        passwordResetting: string;
        phone: string; // TODO: move to shared
        lock: string;
        unlock: string;
        locking: string;
        searchLabel: string;
        searchError: string;
        search: string;
        companyRole: string;
        save: string; // TODO: move to shared,
        lockMemberSuccess: string;
        unlockMemberSuccess: string;
      };
    };
    noRecords: string;
  };
  documents: {
    title: string;
    load: string;
    loadSmall: string;
    confirmMessageChooseDocument: string;
    loaded: string;
    all: string;
    noRecords: string;
    typeTooltip: string;
    types: Translation<TypeEnum>;
    easwaaqTypes: Translation<TypeEnum>;
    buttons: {
      close: string;
    };
  };
  documentsTable: {
    title: string;
    name: string;
    type: string;
    selectType: string;
    date: string;
    chosen: string;
    from: string;
    download: string;
    delete: string;
  };
  additionalFields: {
    title: string;
  };
  btn: ButtonText;
  visibilityStatus: {
    label: string;
    options: Translation<BlockElementShared.StatusEnum>;
  };
  validator: {
    errors: ValidatorErrors;
  };
  errors: Errors;
  form: FormCommonLabels;
  staffBooking: StaffBooking;
}

interface StaffBooking {
  candidates: string;
  candidate: string;
  candidateInfo: string;
  candidateDescription: string;
  candidateName: string;
  candidateOptions: string;
  candidateSkills: string;
  candidatesList: string;
  candidateSettings: string;
  sellerCandidateId: string;
  editCandidateLogo: string;
  candidateSKU: string;
  backToCandidate: string;
  newCandidate: string;
  chooseCandidateCategory: string;
  addNewCandidate: string;
  chats: string;
  chatDetails: string;
  chatReason: string;
  candidateScoreSummary: string;
  candidateRating: string;
}

export interface FormCommonLabels {
  title: string;
  description: string;
  declarationDescription: string;
  sortOrder: string;
  alias: string;
  metaTitle: string;
  metaDescription: string;
  shortDescription: string;
}

export interface Errors {
  defaultError: string;
  alreadyExist: string;
  alreadyExistsGeneric: string;
  internalServerError: string;
  serviceUnavailable: string;
  forbidden: string;
  uniqueConstraint: string;
  requestLimitExceeded: string;
  fileSizeExceed: string;
  chunkLoadError: {
    title: string;
    message: string;
    reloadButton: string;
  };
}

export interface ValidatorErrors {
  notEmptyField: string;
  noSpaces: string;
}

export interface OrderPrintInfo {
  amount: string;
  productsQuantity: string;
  collectorLabel: string;
  date: string;
  deliveryAddress: string;
  deliveryService: string;
  fullName: string;
  label: string;
  order: string;
  orderComments: string;
  orderNumber: string;
  ordersList: string;
  packageType: string;
  phoneNumber: string;
  productName: string;
  trackNumber: string;
}

interface ButtonText {
  cancel: string;
  close: string;
  save: string;
  delete: string;
  download: string;
  update: string;
  upload: string;
  export: string;
  add: string;
  edit: string;
  create: string;
  reset: string;
  next: string;
  deactivate: string;
  show: string;
}

export enum DeliveryMethodProviders {
  'svip-aramex' = 'svip-aramex',
  'svip-ozon' = 'svip-ozon',
  'metaship-v1' = 'metaship-v1',
  'metaship-v2' = 'metaship-v2',
  'manual-delivery' = 'manual-delivery',
  'grab' = 'grab',
  'fully-delivery-support' = 'fully-delivery-support',
  'tender-manual-delivery' = 'tender-manual-delivery',
  'self-pickup-delivery' = 'self-pickup-delivery',
}
